import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { PortalHeader } from "@/components/PortalHeader";
import { PortalFooter } from "@/components/PortalFooter";
import { Stepper } from "@/components/Stepper";
import { useRegistration } from "@/context/RegistrationContext";
import { getCandidateToken, markNeedsOtpReauth, OTP_REAUTH_TIP } from "@/lib/api";
import ContactVerificationStep from "./register/ContactVerificationStep";
import CandidateDetailsStep from "./register/CandidateDetailsStep";
import DocumentsStep from "./register/DocumentsStep";
import PoliciesStep from "./register/PoliciesStep";
import UndertakingStep from "./register/UndertakingStep";
import ReviewStep from "./register/ReviewStep";
import { getRegistrationSteps, normalizeStoredStep } from "@/lib/constants";
import { toast } from "sonner";

export default function RegisterFlow() {
  const nav = useNavigate();
  const { state, update, hydrated } = useRegistration();
  const skipPolicies = !!state.skipPolicies;
  const steps = getRegistrationSteps(skipPolicies);
  const [step, setStep] = useState(() => normalizeStoredStep(state.step || 1, skipPolicies));
  const submittedRedirectChecked = useRef(false);

  // No OTP session (e.g. stale tab after portal update) → back to login with tip.
  useEffect(() => {
    if (getCandidateToken()) return;
    markNeedsOtpReauth();
    toast.info(OTP_REAUTH_TIP, { duration: 10000 });
    nav("/", { replace: true });
  }, [nav]);

  // When the backend hydrates a saved step (e.g. on refresh), jump the flow to it.
  useEffect(() => {
    if (!hydrated) return;
    const normalized = normalizeStoredStep(state.step, skipPolicies);
    if (normalized && normalized !== step) setStep(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, state.step, skipPolicies]);

  // Bounce away to the dashboard if this application was ALREADY submitted
  // before this wizard session started (bookmark, stale link, "Continue
  // registration" on an app that's since been submitted elsewhere) — verified,
  // plain submitted, or flagged for reupload all belong on the dashboard now,
  // never the wizard. Checked exactly once, right after hydration resolves —
  // deliberately NOT reactive to state.submitted after that point, because a
  // live flip to true from THIS session's own Review-step submit is already
  // handled by that step's own explicit navigate to /success; re-checking here
  // would race it and could bounce a fresh submission to /dashboard instead.
  useEffect(() => {
    if (!hydrated || submittedRedirectChecked.current) return;
    submittedRedirectChecked.current = true;
    // Withdrawn belongs on the dashboard too, submitted or not — the wizard
    // rejects further edits/submission server-side regardless, but bouncing
    // here avoids letting a withdrawn candidate sit in a form that can't go
    // anywhere.
    if (state.submitted || state.status === "withdrawn") nav("/dashboard", { replace: true });
  }, [hydrated, state.submitted, state.status, nav]);

  useEffect(() => {
    if (!state.applicationNumber) {
      // On a hard refresh the URL may still carry ?app=<no> — give AppNoSync a
      // chance to hydrate before bouncing back to landing.
      const urlAppNo = new URLSearchParams(window.location.search).get("app");
      if (!urlAppNo) nav("/", { replace: true });
    }
  }, [state.applicationNumber, nav]);

  useEffect(() => {
    if (!hydrated || !skipPolicies || step !== 4) return;
    setStep(5);
    update({ step: 5 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, skipPolicies, step]);

  const go = (s) => {
    setStep(s);
    update({ step: s });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentStepMeta = steps.find((s) => s.id === step) || steps[0];
  const stepIndex = Math.max(1, steps.findIndex((s) => s.id === step) + 1);

  if (state.applicationNumber && !hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F7] text-[#6B7280] text-sm">
        Loading your saved registration…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
      <PortalHeader />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="text-center mb-8">
            <div className="tiny-label">Registration Portal · 2026</div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#111] mt-2">
              {currentStepMeta?.label}
            </h1>
            <p className="text-[#6B7280] mt-2">
              Step {stepIndex} of {steps.length} · Your progress is automatically saved.
            </p>
          </div>
          <Stepper current={step} skipPolicies={skipPolicies} />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {step === 1 && <ContactVerificationStep onNext={() => go(2)} />}
              {step === 2 && <CandidateDetailsStep onNext={() => go(3)} onBack={() => go(1)} />}
              {step === 3 && (
                <DocumentsStep onNext={() => go(skipPolicies ? 5 : 4)} onBack={() => go(2)} />
              )}
              {step === 4 && !skipPolicies && (
                <PoliciesStep onNext={() => go(5)} onBack={() => go(3)} />
              )}
              {step === 5 && (
                <UndertakingStep onNext={() => go(6)} onBack={() => go(skipPolicies ? 3 : 4)} />
              )}
              {step === 6 && <ReviewStep onBack={() => go(5)} onEdit={(s) => go(normalizeStoredStep(s, skipPolicies))} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <PortalFooter />
    </div>
  );
}
